import { useState, useEffect } from 'react';
import { listAggregatedEvents } from '../../services/api/eventAggregationService';

/**
 * Hook for handling event pagination
 * Current page automatically updates based on total events loaded
 */
export function useEventPagination({ events, setEvents, setError }) {
  const [hasMore, setHasMore] = useState(false); // Start false, polling will set to true when nextToken available
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const [hasPaginated, setHasPaginated] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  // Auto-update current page based on scroll position
  useEffect(() => {
    const scrollableDiv = document.getElementById('aggregatorScrollableDiv');
    if (!scrollableDiv) return;

    const handleScroll = () => {
      const firstRow = scrollableDiv.querySelector('tbody tr');
      if (!firstRow || events.length === 0) return;

      const rowHeight = firstRow.offsetHeight;
      const scrollTop = scrollableDiv.scrollTop;

      // Calculate which page we're viewing based on scroll position
      const currentRowIndex = Math.floor(scrollTop / rowHeight);
      const calculatedPage = Math.floor(currentRowIndex / pageSize) + 1;

      if (calculatedPage !== currentPage && calculatedPage > 0) {
        setCurrentPage(calculatedPage);
      }
    };

    scrollableDiv.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial calculation

    return () => {
      scrollableDiv.removeEventListener('scroll', handleScroll);
    };
  }, [events.length, pageSize, currentPage]);

  const loadMoreEvents = async () => {
    if (!hasMore || loadingMore) return;
    if (!nextToken) return;

    setHasPaginated(true);
    setLoadingMore(true);

    try {
      const response = await listAggregatedEvents({ limit: pageSize, nextToken });

      setEvents(prevEvents => {
        const existingIds = new Set(prevEvents.map(e => e.eventId));
        const newEvents = (response.events || []).filter(e => !existingIds.has(e.eventId));
        return [...prevEvents, ...newEvents];
      });

      setNextToken(response.nextToken || null);
      setHasMore(!!response.nextToken);
    } catch (err) {
      console.error('[Pagination] Error loading more events:', err);
      setError(err.message || 'Failed to load more events');
    } finally {
      setLoadingMore(false);
    }
  };

  const goToNextPage = () => {
    const scrollableDiv = document.getElementById('aggregatorScrollableDiv');
    if (!scrollableDiv) return;

    // Get approximate height of one event row (we'll measure first row)
    const firstRow = scrollableDiv.querySelector('tbody tr');
    if (!firstRow) return;

    const rowHeight = firstRow.offsetHeight;
    const scrollAmount = rowHeight * pageSize;

    scrollableDiv.scrollBy({ top: scrollAmount, behavior: 'smooth' });
  };

  const goToPreviousPage = () => {
    const scrollableDiv = document.getElementById('aggregatorScrollableDiv');
    if (!scrollableDiv) return;

    // Get approximate height of one event row
    const firstRow = scrollableDiv.querySelector('tbody tr');
    if (!firstRow) return;

    const rowHeight = firstRow.offsetHeight;
    const scrollAmount = rowHeight * pageSize;

    scrollableDiv.scrollBy({ top: -scrollAmount, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    // Keep current events but recalculate page number
    const newPage = Math.ceil(events.length / newSize);
    setCurrentPage(newPage);
  };

  const resetPagination = () => {
    setHasMore(true);
    setNextToken(null);
    setHasPaginated(false);
    setEvents([]);
    setCurrentPage(1);
  };

  return {
    hasMore,
    loadingMore,
    nextToken,
    hasPaginated,
    pageSize,
    currentPage,
    setPageSize: handlePageSizeChange,
    setNextToken,
    setHasMore,
    loadMoreEvents,
    goToNextPage,
    goToPreviousPage,
    resetPagination
  };
}
